/**
 * Owner: Masa Bayu
 * File: spsu-ocr-plate-gate.ts
 * Date:Time: 2026-10-04 23:01
 * Description: OCR plate geometry + tone gate · white softmask body lines.
 */

import type { ImageElement } from '../extract/spsu-types';

/** Text-strip softmask heuristic · include short end-of-line crumbs (ghost plates). */
export const MAX_LINE_HEIGHT_PT = 90;
export const MIN_LINE_ASPECT = 0.35;
/**
 * White softmask body lines measure paintTone=light (opaque RGB≈255).
 * Strong line geometry still admits them as OCR candidates (then ghost-drop).
 */
export const STRONG_OCR_LINE_ASPECT = 5;
export const STRONG_OCR_LINE_HEIGHT_MIN_PT = 10;
export const STRONG_OCR_LINE_HEIGHT_MAX_PT = 20;

/** Eyes diagnostic · null = geometry accepted. */
export function bodyGlyphLineGeometryRejection(image: ImageElement): string | null {
  const { width, height } = image.bbox;
  if (!(height > 0)) return 'height_non_positive';
  if (height > MAX_LINE_HEIGHT_PT) {
    return `height_too_tall:${height.toFixed(1)}>${MAX_LINE_HEIGHT_PT}`;
  }
  const aspect = width / Math.max(height, 1e-6);
  if (aspect < MIN_LINE_ASPECT) {
    return `aspect_too_narrow:${aspect.toFixed(3)}<${MIN_LINE_ASPECT}`;
  }
  return null;
}

/** Line-strip geometry only · height/aspect · shared by dark OCR + ghost drop. */
export function isBodyGlyphLineGeometry(image: ImageElement): boolean {
  return bodyGlyphLineGeometryRejection(image) === null;
}

/** Eyes diagnostic · null = text-line OCR gate accepted. */
export function textLinePlateRejectionReason(
  image: ImageElement,
  tone: string | undefined,
): string | null {
  const geo = bodyGlyphLineGeometryRejection(image);
  if (geo !== null) return geo;

  if (tone === 'dark') return null;

  const { width, height } = image.bbox;
  const aspect = width / Math.max(height, 1e-6);
  const strongLightOrGray =
    (tone === 'light' || tone === 'gray') &&
    aspect >= STRONG_OCR_LINE_ASPECT &&
    height >= STRONG_OCR_LINE_HEIGHT_MIN_PT &&
    height <= STRONG_OCR_LINE_HEIGHT_MAX_PT;
  if (strongLightOrGray) return null;

  return `tone_not_dark:${tone ?? 'unset'}`;
}

/** Dark imprint · or strong white softmask body line → OCR candidate. */
export function isTextLinePlate(image: ImageElement, tone: string | undefined): boolean {
  return textLinePlateRejectionReason(image, tone) === null;
}

/**
 * Light/gray/unset line photostat that paints white over cream · drop when over live OCR.
 * Dark plates stay for transparent-imprint path. Chroma art kept. Ivy/cream fail geometry.
 */
export function isGhostBodyLinePlate(
  image: ImageElement,
  tone: string | undefined,
): boolean {
  if (bodyGlyphLineGeometryRejection(image) !== null) return false;
  return tone === 'light' || tone === 'gray' || tone == null;
}
